// 3rd party libraries
import React, { ReactNode, useEffect, useState } from 'react';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';

// Components
import TableForm from '../Form';
import { StyledIconCellText, StyledIconCell } from '../Cell';
import { Header } from '../Headers';
import { BaseSelect, RadioButton } from 'components';
import { CustomContentRenderer, CustomDropdownRenderer } from '../Dropdown';
import { RowButtonGroup, StyledCrudButtonWrapper } from '../Row';

// Types
import { ColumnConfig } from 'types';
import { TableRowProps, WidgetTableDataItem, WidgetTableDropdownItem } from 'types/machine-health';
import { WidgetType } from 'types/protein';
import { Order } from 'types/machine-health/widget-table';

// Helpers
import { updateLocalItem } from '.';

// Constants
import { TagIdPrefix, tagsColorSetterEnabled, WidgetTypeToIconMap } from 'constants/machineConfig';
import { Swatch } from '../Swatch';
import { SwatchList } from '../SwatchList';
import { styledTheme } from 'common/theme';

import { configuratorColorsPallete } from 'common/theme/light.theme';
import ChevronDown from 'icons/indexed-icons/ChevronDown';
import { useWidgetTableContext } from '../WidgetTableContext';
import { ToastMsg } from 'common/components/Toast';

// Styled Components
const StyledSelectWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 1rem;
`;

const EditSwatchContainer = styled.div`
  position: relative;
  .color-btn-wrapper {
    display: flex;
    align-items: center;
    .dropdown-button {
      display: flex;
      background: none;
      border: 0;
      cursor: pointer;
    }
  }
  .list {
    button {
      cursor: pointer;
    }
  }
`;

const colorsList = Object.values(configuratorColorsPallete);

export interface EditConfigArgs {
  columnConfigs: ColumnConfig[];
  dataSize: number;
  editRow: string | null;
  setEditRow: React.Dispatch<React.SetStateAction<string | null>>;
  setIsDirty?: React.Dispatch<React.SetStateAction<boolean>>;
  setLocalTableRows: React.Dispatch<React.SetStateAction<WidgetTableDataItem[]>>;
  shouldRenderTags: boolean;
  tabId: string;
  tags?: WidgetTableDropdownItem[] | null;
}

interface typedInfoProps {
  actions: {
    props: {
      itemIds: {
        id: string;
        tabId?: string;
        widgetGroupId?: string;
        widgetId?: string;
      };
    };
  };
  tagType?: string;
  key?: string;
  isTag?: boolean;
  widgetType?: WidgetType;
  unit?: string;
  meta?: {
    dataType?: string;
  };
  children?: WidgetTableDataItem[] | null;
}

interface EditProps {
  initValue?: string;
  handleUpdate?: (value: string | number) => void;
  fieldName?: string;
  setIsDirty?: React.Dispatch<React.SetStateAction<boolean>>;
  typedProps?: typedInfoProps;
  handleColor: (value: string | number) => void;
}

const EditDisplaySwatch = ({ setIsDirty, handleColor, typedProps }: EditProps): JSX.Element => {
  const { selectedColorItem, updateSelectedColor, localTableRows, setSelectedColorItem } =
    useWidgetTableContext();
  const { actions, widgetType, isTag, key, tagType, unit, meta, children, ...cleanedTypedProps } =
    typedProps || {};

  const currentId = cleanedTypedProps?.id;
  const defaultInitValue = cleanedTypedProps?.color || '#000000';
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [colorValue, setColorValue] = useState<string>(defaultInitValue);

  const updatedTags = localTableRows?.[0]?.tags?.map((tag: { id?: string; color?: string }) => {
    const match = selectedColorItem.find((item) => item.tag === tag.id);
    return match ? { ...tag, color: match.color } : tag;
  }) || [];


  const usedColors = React.useMemo(() => {
    return updatedTags.map((tag: { color?: string }) => tag.color).filter(Boolean);
  }, [updatedTags]);

  const preSelectedColor = React.useMemo(() => {
    return selectedColorItem.map(({ color, tag }) => ({
      color,
      tag,
    }));
  }, [selectedColorItem]);

  const disabledColors = [...new Set(usedColors)];

  const handleColorClick = (selectedColor: string) => {
    if (selectedColor === colorValue || disabledColors.includes(selectedColor)) return;

    setColorValue(selectedColor);
    handleColor({ ...cleanedTypedProps, color: selectedColor });
    setIsDirty?.(true);

    if (currentId) {
      updateSelectedColor(currentId, selectedColor);
    }
  };

  return (
    <EditSwatchContainer>
      <div className="color-btn-wrapper">
        <Swatch color={colorValue} fieldName="color" />
        <button className="dropdown-button" onClick={() => setIsOpen(!isOpen)}>
          <ChevronDown color={styledTheme.colors.gray400} width={16} height={16} />
        </button>
      </div>

      {isOpen && (
        <SwatchList
          colors={colorsList}
          onColorClick={handleColorClick}
          disabledColors={disabledColors}
        />
      )}
    </EditSwatchContainer>
  );
};

/** axis start*/

interface DisplayAxisSectionProps {
  selectedAxis?: boolean;
  onAxisChange?: (rowID: string, axis: boolean) => void;
  rowID?: string;
}

const DisplayAxisContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;
  gap: 5px;
  justify-content: center;
  align-items: center;
  .radio-group-section {
    gap: 1rem;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 3rem;
  font-weight: 400 !important;

  .radio-label {
    font-weight: 400 !important;
  }
`;

export const DisplayAxisSection: React.FC<DisplayAxisSectionProps> = ({
  selectedAxis,
  onAxisChange,
  rowID
}) => {
  const handleAxisChange = (axis: boolean) => {
    if (onAxisChange) {
      onAxisChange(rowID, axis);
    }
  };

  if (!rowID) return <></>;
  return (
    <DisplayAxisContainer>
      <RadioGroup className="radio-group-section">
        <RadioButton
          onChange={() => handleAxisChange(true)}
          label="L_Axis"
          labelWeight={600}
          verticalLayout={false}
          id={`${rowID}_left`}
          name={rowID}
          checked={selectedAxis === true}
        />
        <RadioButton
          onChange={() => handleAxisChange(false)}
          label="R_Axis"
          labelWeight={600}
          verticalLayout={false}
          name={rowID}
          id={`${rowID}_right`}
          checked={selectedAxis === false}
        />
      </RadioGroup>
    </DisplayAxisContainer>
  );
};

interface EditAxisDisplaySwatchProps {
  onAxisClicked?: (value: string | number) => void;
  setIsDirty?: React.Dispatch<React.SetStateAction<boolean>>;
  typedProps?: typedInfoProps;
}

const EditAxisDisplaySwatch = ({
  onAxisClicked,
  setIsDirty,
  typedProps
}: EditAxisDisplaySwatchProps) => {
  const { axisData, setAxisData } = useWidgetTableContext();

  const { actions, widgetType, isTag, key, tagType, unit, meta, children, ...cleanedAxisTypedProps } = typedProps || {};
  const currentId = cleanedAxisTypedProps?.id;
  const initialAxisValue =
    typeof cleanedAxisTypedProps?.isLeftSide === 'boolean'
      ? cleanedAxisTypedProps.isLeftSide
      : true;

  const [selectedAxis, setSelectedAxis] = useState<boolean>(initialAxisValue);

  useEffect(() => {
    if (typeof cleanedAxisTypedProps?.isLeftSide === 'boolean') {
      setSelectedAxis(cleanedAxisTypedProps.isLeftSide);
      setAxisData({ isLeftSide: cleanedAxisTypedProps.isLeftSide });
    }
  }, [cleanedAxisTypedProps?.isLeftSide]);

   const handleAxisChangeData = (rowID: string, axis: boolean) => {
    if (!rowID) return;
      setSelectedAxis(axis);
      setIsDirty?.(true);
      onAxisClicked?.({ ...cleanedAxisTypedProps, isLeftSide: axis });
      if (currentId) {
        setAxisData(currentId, { isLeftSide: axis });
      }
  };

  if (!currentId) return null;

  return (
    <div>
      <DisplayAxisSection
        onAxisChange={handleAxisChangeData}
        rowID={currentId}
        selectedAxis={selectedAxis}
      />
    </div>
  );
};

/** axis end*/

export const editColumnConfigs = ({
  columnConfigs,
  dataSize = 0,
  editRow,
  setEditRow,
  setIsDirty,
  setLocalTableRows,
  shouldRenderTags,
  tabId,
  tags
}: EditConfigArgs): ColumnConfig[] => {
  // Define submit callback to pass into form components
  const handleSubmit = (
    value: string | WidgetTableDropdownItem,
    itemIds: Record<string, string>
  ) => {
    if (!value) {
      ToastMsg({
        heading: 'Error',
        message: 'Widget name is required',
        type: 'error',
        position: 'top-center',
      });
      return;
    }
    setLocalTableRows?.((prev) =>
      updateLocalItem({
        prev,
        itemIds,
        shouldRenderTags,
        tabId,
        updateKey: 'name',
        updateValue: value
      })
    );

    setIsDirty?.(true);
    setEditRow(null);
  };

  // Method to perform the reodering of tags.
  // Reordering of tag groups and matrix widgets is accomplished in
  // src/components/machine-health/configurator/WidgetTable/Row.tsx
  // by a similar method.
  const handleOrderChange = (
    order: Order,
    itemIds: {
      id: string;
      tabId?: string | undefined;
      tagGroupId?: string | undefined;
      widgetGroupId?: string | undefined;
      widgetId?: string | undefined;
    }
  ) => {
    setLocalTableRows?.((prev) => {
      const row = prev.find((p) => p.id === tabId);

      if (!row || !row.members) return prev;

      const members = row.members.find((m) => m.id === itemIds.widgetId);

      const nestedMembers = row.members[0]?.members?.find(
        (member) => member.id === itemIds.widgetId
      );
      const nestedTagGroups = nestedMembers?.members?.find(
        (tagGroup) => tagGroup.id === itemIds.tagGroupId
      );

      const tagGroups = members?.members?.find((tagGroup) => tagGroup.id === itemIds.tagGroupId);

      const reorderTags = (
        currentTag: WidgetTableDropdownItem,
        items: WidgetTableDropdownItem[]
      ) => {
        const indexCurrentTag = items.indexOf(currentTag);

        const elm = items.splice(indexCurrentTag, 1)[0];
        items.splice(
          order === Order.DOWN
            ? Math.min(items.length + 1, indexCurrentTag + 1)
            : Math.max(0, indexCurrentTag - 1),
          0,
          elm
        );
      };

      if (tagGroups?.tags) {
        const currentTag = tagGroups.tags.find((tagGroup) => tagGroup.id === itemIds.id);

        if (!currentTag) return prev;

        reorderTags(currentTag, tagGroups.tags);
      } else if (members?.tags) {
        const currentTag = members.tags.find((d) => d.id === itemIds.id);

        if (!currentTag) return prev;

        reorderTags(currentTag, members.tags);
      } else if (nestedTagGroups?.tags) {
        const currentTag = nestedTagGroups.tags.find((tagGroup) => tagGroup.id === itemIds.id);
        if (!currentTag) return prev;
        reorderTags(currentTag, nestedTagGroups.tags);
      } else {
        return prev;
      }

      return updateLocalItem({
        prev,
        itemIds,
        shouldRenderTags: false,
        tabId
      });
    });

    setIsDirty?.(true);
  };

  const nextConfigs = [...columnConfigs];

  // Add the actions column
  nextConfigs.push({
    dataIndex: 'actions',
    key: 'actions',
    title: Header({
      title: dataSize > 0 ? 'Actions' : ''
    }),
    width: 100
  });

  // Add the expand button column
  nextConfigs.push({
    dataIndex: 'expand',
    key: 'expand',
    title: <span></span>,
    width: 0
  });

  // Iterate over the columns, if we come across a row that is being edited, ...
  // ... we want to render a form and change our column-span properties ...
  // ... (the form needs extra space, and some column data is hidden during an edit)
  const updatedColumnConfigs = nextConfigs.map((colCfg, configIndex) => {
    return {
      ...colCfg,
      render(item: string | number | boolean | JSX.Element, rowProps: unknown) {
        const typedProps = rowProps as TableRowProps;        

        // collect the edit state
        const isEditing = editRow === typedProps.id;
        const isTagTable = shouldRenderTags && typedProps.widgetType === WidgetType.State;

        const {
          actions,
          id: selectedId,
          name: selectedName,
          widgetType: selectedType,
          values: selectedValues
        } = typedProps ?? { actions: null, id: '', name: '--', type: WidgetType.State, values: [] };
        const { props } = actions ?? { props: null };
        const { itemIds } = props ?? { itemIds: null };

        // render map -> we will use our current column index to look up ...
        // ... what we should be rendering during a given table state
        const renderMap: Record<number, JSX.Element | null> = {
          // In the first column, add the display icon to the first column
          0: WidgetTypeToIconMap[typedProps.widgetType] ? (
            <StyledIconCell>
              <FontAwesomeIcon
                icon={WidgetTypeToIconMap[typedProps.widgetType] as IconDefinition}
              />
              <StyledIconCellText>{item}</StyledIconCellText>
            </StyledIconCell>
          ) : null,
          // color column render
          2: tagsColorSetterEnabled && isTagTable && tags && (tags?.length || 0) > 0 ? (
              <>
                <EditDisplaySwatch
                  typedProps={typedProps}
                  handleColor={(color) => {
                    setLocalTableRows?.((prev) =>
                      updateLocalItem({
                        prev,
                        itemIds,
                        shouldRenderTags,
                        tabId,
                        updateKey: 'color',
                        updateValue: color
                      })
                    );
                    setIsDirty?.(true);
                    setEditRow(null);
                  }}
                  setIsDirty={setIsDirty}
                />
              </>
            ) : isTagTable && tags && (tags?.length || 0) > 0 ? (
              <StyledSelectWrapper>
                <BaseSelect
                  contentRenderer={(item: Record<string, unknown>) => (
                    <CustomContentRenderer item={item as unknown as WidgetTableDropdownItem} />
                  )}
                  dropdownRenderer={(props, state, methods) => (
                    <CustomDropdownRenderer props={props} state={state} methods={methods} />
                  )}
                  disabledLabel="Tag already assigned"
                  handleChangeSearch={(value: (string | Record<string, unknown> | undefined)[]) => {
                    // We need to account for the case when value = undefined ...
                    // ... which can happen when the user clears the search field
                    const updateValue = value[0] as unknown as WidgetTableDropdownItem | undefined;
                    return updateValue && handleSubmit(updateValue, itemIds);
                  }}
                  labelField="id"
                  options={tags as { id: string; label: string }[]}
                  placeholder="Select a tag..."
                  searchable
                  searchBy="label" // label is assigned as friendlyName value
                  value={
                    !selectedId?.startsWith(TagIdPrefix)
                      ? {
                          label: selectedName,
                          id: selectedId,
                          name: selectedName || selectedId,
                          type: selectedType,
                          values: selectedValues
                        }
                      : ''
                  }
                  valueField="id"
                />
                <RowButtonGroup>
                  <StyledCrudButtonWrapper>
                    <FontAwesomeIcon
                      icon={faArrowDown}
                      onClick={() => handleOrderChange(Order.DOWN, itemIds)}
                    />
                  </StyledCrudButtonWrapper>
                  <StyledCrudButtonWrapper>
                    <FontAwesomeIcon
                      icon={faArrowUp}
                      onClick={() => handleOrderChange(Order.UP, itemIds)}
                    />
                  </StyledCrudButtonWrapper>
                </RowButtonGroup>
              </StyledSelectWrapper>
            ) : isEditing ? (
              <TableForm
                cancelCallback={() => {
                  setEditRow(null);
                }}
                id={itemIds.id}
                item={typedProps?.name as string}
                placeholder="Widget name"
                submitCallback={(value) => handleSubmit(value, itemIds)}
              />
            ) : null,
            3:
            tagsColorSetterEnabled && isTagTable && tags && (tags?.length || 0) > 0 ? (
              <EditAxisDisplaySwatch
                typedProps={typedProps}
                onAxisClicked={(axis) => {
                  setLocalTableRows?.((prev) =>
                      updateLocalItem({
                        prev,
                        itemIds,
                        shouldRenderTags,
                        tabId,
                        updateKey: 'isLeftSide',
                        updateValue: axis
                      })
                    );
                }}
                setIsDirty={setIsDirty}
              />
            ) : null,
          // if the row is being edited, render a form instead of data at index 1
          4: tagsColorSetterEnabled ? 
          (isTagTable && tags && (tags?.length || 0) > 0 ? (
              <StyledSelectWrapper>
                <BaseSelect
                  contentRenderer={(item: Record<string, unknown>) => (
                    <CustomContentRenderer item={item as unknown as WidgetTableDropdownItem} />
                  )}
                  dropdownRenderer={(props, state, methods) => (
                    <CustomDropdownRenderer props={props} state={state} methods={methods} />
                  )}
                  disabledLabel="Tag already assigned"
                  handleChangeSearch={(value: (string | Record<string, unknown> | undefined)[]) => {
                    // We need to account for the case when value = undefined ...
                    // ... which can happen when the user clears the search field
                    const updateValue = value[0] as unknown as WidgetTableDropdownItem | undefined;
                    return updateValue && handleSubmit(updateValue, itemIds);
                  }}
                  labelField="id"
                  options={tags as { id: string; label: string }[]}
                  placeholder="Select a tag..."
                  searchable
                  searchBy="label" // label is assigned as friendlyName value
                  value={
                    !selectedId?.startsWith(TagIdPrefix)
                      ? {
                          label: selectedName,
                          id: selectedId,
                          name: selectedName || selectedId,
                          type: selectedType,
                          values: selectedValues
                        }
                      : ''
                  }
                  valueField="id"
                />
                <RowButtonGroup>
                  <StyledCrudButtonWrapper>
                    <FontAwesomeIcon
                      icon={faArrowDown}
                      onClick={() => handleOrderChange(Order.DOWN, itemIds)}
                    />
                  </StyledCrudButtonWrapper>
                  <StyledCrudButtonWrapper>
                    <FontAwesomeIcon
                      icon={faArrowUp}
                      onClick={() => handleOrderChange(Order.UP, itemIds)}
                    />
                  </StyledCrudButtonWrapper>
                </RowButtonGroup>
              </StyledSelectWrapper>
            ) : isEditing ? (
              <TableForm
                cancelCallback={() => {
                  setEditRow(null);
                }}
                id={itemIds.id}
                item={typedProps?.name as string}
                placeholder="Widget name"
                submitCallback={(value) => handleSubmit(value, itemIds)}
              />
            ) : null ) : null
        };

        // look up our render alternate based on which column we're in ...
        // ... if nothing is found, return the item as is (render text per column as usual)
        return (renderMap[configIndex] || item) as Element | ReactNode;
      },
      onCell: (rowData: Record<string, string>) => {
        // collect the edit state
        const isEditing = editRow === rowData.id;
        const isTagTable = shouldRenderTags && rowData.widgetType === WidgetType.State;

        // init new props: we will return this at the end of the function
        // https://table-react-component.vercel.app/demo/colspan-rowspan
        const props: React.TdHTMLAttributes<HTMLTableCellElement> = {};

        if(!tagsColorSetterEnabled) {
          if (isEditing || isTagTable) {
            // update column span values based on index ->
            // extra space for index 2 (name), hide indeces 1 (tags), and 3 (actions)
            if (configIndex === 2) {
              props.colSpan = 3;
            }
            if (configIndex === 1 || configIndex === 4) {
              props.colSpan = 0;
            }
          }
        }else {
          if (isEditing || isTagTable) {
            // update column span values based on index ->
            // extra space for index 2 (name), hide indeces 1 (tags), and 3 (actions)
            if (configIndex === 3) {
              props.colSpan = 1;
            }
            if (configIndex === 1 || configIndex === 4) {
              props.colSpan = 0;
            }
          }
        }

        // For rows that aren't of subtype 'matrix-widget-group' or are without children, ...
        // ... we want to remove the spacing created by the expand button ...
        // ... since it isn't present on child rows. To do this, we will ...
        // ... have the second to last child take up two col-spans, and the last none.
        // ... we only do this when we are not in edit mode

        if(!tagsColorSetterEnabled) {
          if ((!rowData.children?.length || !rowData.canBeExpanded) && !isEditing) {
            if (configIndex === 3) {
              props.colSpan = 2;
            } else if (configIndex === 4) {
              props.colSpan = 0;
            }
          }
        }else {
          if ((!rowData.children?.length || !rowData.canBeExpanded) && !isEditing) {
            if (configIndex === 4) {
              props.colSpan = 2;
            } else if (configIndex === 5) {
              props.colSpan = 2;
            }else if (configIndex === 6) {
              props.colSpan = 0;
            }
          }
        }

        return props;
      }
    };
  });

  return updatedColumnConfigs;
};
